import React from 'react';
import classNames from 'classnames/bind';

import { PricingPlan } from 'types/pricing_plan';

import { useSpringState } from 'hooks/useSpringState';
import { useSwitchPricingPlan } from 'organizations/hooks/useSwitchPricingPlan';

import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Button } from 'components/Button/Button';
import { FormMessage } from 'components/FormMessage/FormMessage';
import * as Dialog from 'components/Dialog';

import styles from './UpgradePlanDialog.module.scss';

const c = classNames.bind(styles);

export type UpgradePlanFormProps = {
  planID: PricingPlan['id'];
  onSuccess: () => void;
  submitLabel?: string;
};

export function UpgradePlanForm({
  planID,
  onSuccess,
  submitLabel = 'Upgrade and pay',
}: UpgradePlanFormProps) {
  const [isSuccess, setIsSuccess] = useSpringState(false, 3500);
  const hasHandledSuccessRef = React.useRef(false);

  const handleSuccess = React.useCallback(() => {
    if (hasHandledSuccessRef.current) {
      return;
    }

    hasHandledSuccessRef.current = true;
    setIsSuccess(true);
    onSuccess();
  }, [onSuccess, setIsSuccess]);

  const {
    error,
    isLoading,
    mutate: switchPricingPlan,
  } = useSwitchPricingPlan({
    onSuccess: handleSuccess,
  });

  async function handleUpgradeClick() {
    switchPricingPlan(planID);
  }

  return (
    <>
      {isSuccess && <Dialog.Message intent="success">Success!</Dialog.Message>}

      {error && (
        <FormMessage icon="warning" intent="danger">
          {error.message}
        </FormMessage>
      )}

      <div className={c('upgrade-buttons')}>
        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            onClick={handleUpgradeClick}
            loading={isLoading}
          >
            {submitLabel}
          </Button>

          <Dialog.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Dialog.Close>
        </ButtonGroup>
      </div>
    </>
  );
}
